import { partitionHandoffQuestions } from "./handoffQuestionDisplay";
import { promiseText, sourceTrustLabel } from "./workBriefGuidedHelpers";

export const TASK_BUILDER_SUGGESTIONS = [
  "Improve an existing feature",
  "Build something new",
  "Fix a customer problem",
  "Review a product idea",
];

export const TASK_BUILDER_QUESTION_CHOICES = [
  { id: "recommend", label: "Use your recommendation" },
  { id: "unsure", label: "I’m not sure" },
];

export const TASK_SECTION_LABELS = {
  outcome: "Outcome",
  requirements: "What to build",
  decisions: "Product decisions",
  exclusions: "What is not included",
  successChecks: "Success checks",
  risks: "Risks and deferrals",
  sources: "What this Task is based on",
};

export const MATERIAL_QUESTION_RE =
  /user-visible|affected user|compatib|existing|data|permission|secur|fail|error|startup|scope|boundar|rollout|verif|test|schema|request|response|who|which users|behavior/i;

export function suggestedAnswersForQuestion(question) {
  const text = String(question?.text || question?.displayText || "").toLowerCase();
  if (/request|response|schema/.test(text) && /or both/.test(text)) {
    return [
      { id: "request", label: "Request schemas only" },
      { id: "response", label: "Response schemas only" },
      { id: "both", label: "Both" },
    ];
  }
  return [];
}

function inferMutationSection(lower) {
  if (/risk|deferr/.test(lower)) return "risks";
  if (/source|screenshot|prd|document|example|primary source/.test(lower)) return "sources";
  if (/outcome/.test(lower)) return "outcome";
  if (/success check|how success/.test(lower)) return "successChecks";
  if (/not included|exclu/.test(lower)) return "exclusions";
  if (/decision/.test(lower)) return "decisions";
  if (/requirement|compatib|documentation/.test(lower)) return "requirements";
  return "requirements";
}

export function classifyFounderUtterance(text) {
  const value = String(text || "").trim();
  const lower = value.toLowerCase();
  if (!value) return { kind: "empty" };
  if (/why (are you|is this|does this|do you)|why this matters|ask ai to explain/.test(lower)) {
    return { kind: "why" };
  }
  if (/what would you recommend|recommend (an |one)?answer|what do you (think|recommend)|use your recommendation/.test(lower)) {
    return { kind: "recommend" };
  }
  if (/what are we (still )?missing|what.?s still (unclear|missing)|what remains/.test(lower)) {
    return { kind: "missing" };
  }
  if (/show me what changed|what changed in the task/.test(lower)) {
    return { kind: "show_changed" };
  }
  if (/can the repository answer|does the (repo|code|repository)/.test(lower)) {
    return { kind: "repo" };
  }
  if (/keep this simpler|too many questions|less technical/.test(lower)) {
    return { kind: "simpler" };
  }
  if (/not part of (the )?feature|out of scope|not relevant/.test(lower)) {
    return { kind: "not_relevant" };
  }
  if (/i.?m not sure|not sure yet/.test(lower)) {
    return { kind: "unsure" };
  }
  if (/change my previous|actually[, ]|make it .+ only|revise/.test(lower)) {
    return { kind: "revise", section: inferMutationSection(lower), text: value };
  }
  if (/what is still unclear|what's still unclear/.test(lower)) {
    return { kind: "unclear" };
  }
  if (/show me the (full )?task|task so far/.test(lower)) {
    return { kind: "show_task" };
  }
  if (/show me what this is based on|what is this based on/.test(lower)) {
    return { kind: "sources" };
  }
  if (/^defer$|defer this( decision)?$/.test(lower)) {
    return { kind: "defer" };
  }
  if (
    /^(add|remove|change|update|delete|drop|treat|use|defer)\b/.test(lower) ||
    /\b(add|remove|change|update|delete)\b.+\b(requirement|risk|deferr|source|outcome|success|documentation)\b/.test(lower) ||
    /treat this as an example|only an example|primary source|not a requirement/.test(lower)
  ) {
    return { kind: "mutate", section: inferMutationSection(lower), text: value };
  }
  if (/ready to send|send (this )?to engineering/.test(lower)) {
    return { kind: "ready" };
  }
  return { kind: "answer", text: value };
}

export function understandingFromRequest(requestText) {
  const text = String(requestText || "").replace(/\s+/g, " ").trim();
  if (!text) {
    return {
      outcome: "You have not described the work yet.",
      people: "People affected are not clear yet.",
      behavior: "Important behavior is still open.",
      uncertain: "Add a short description so I can ask the right questions.",
    };
  }
  return {
    outcome: text.length > 220 ? `${text.slice(0, 217).trim()}…` : text,
    people: /user|customer|engineer|operator|admin/i.test(text)
      ? "People mentioned in your request stay in scope."
      : "Anyone using this part of the product.",
    behavior: /case|schema|compatib|fail|error|timeout/i.test(text)
      ? "The behavior you described should stay consistent for existing use."
      : "The product should do what you described, without silent extras.",
    uncertain: /without breaking|existing/i.test(text)
      ? "Compatibility with existing users still needs a precise decision."
      : "I will ask only if another decision would change the work.",
  };
}

export function currentFounderQuestion(draft, requestText) {
  const questions = Array.isArray(draft?.unresolvedQuestions) ? draft.unresolvedQuestions : [];
  const open = questions.filter((q) => !q.status || q.status === "open");
  const partitioned = partitionHandoffQuestions({
    serverQuestions: open,
    requestText,
  });
  const founder = partitioned.founderQuestions || partitioned.founder || open;
  const next = Array.isArray(founder) ? founder[0] : null;
  const remaining = Math.max(0, (Array.isArray(founder) ? founder.length : 0) - (next ? 1 : 0));
  return { next, remaining };
}

function markerForOrigin(origin) {
  if (origin === "human_added") return "Confirmed by you";
  if (origin === "deterministic_scaffold") return "From screenshot";
  if (origin === "model") return "Suggested by AI";
  return "Found in repository";
}

export function taskSoFarFromDraft(draft, requestText, plan) {
  const criteria = Array.isArray(draft?.criteria) ? draft.criteria : [];
  const assumptions = Array.isArray(draft?.assumptions) ? draft.assumptions : [];
  const nonGoals = Array.isArray(draft?.nonGoals) ? draft.nonGoals : [];
  const { next, remaining } = currentFounderQuestion(draft, requestText);
  const understanding = understandingFromRequest(requestText || plan?.featureRequestText);
  return {
    outcome: plan?.planIntelligence?.goal || understanding.outcome,
    scope: criteria.slice(0, 8).map((item) => ({
      text: promiseText(item) || item.action || item.text,
      marker: markerForOrigin(item.origin),
    })),
    notIncluded: nonGoals.slice(0, 6).map((item) => ({
      text: item.text || item.statement || String(item),
      marker: "Needs confirmation",
    })),
    decisions: assumptions
      .filter((item) => item.status === "accepted" || item.origin === "human_added")
      .slice(0, 8)
      .map((item) => ({
        text: item.text || item.statement,
        marker: "Confirmed by you",
      })),
    success: (plan?.acceptanceCriteria || []).slice(0, 5).map((item) =>
      typeof item === "string" ? item : item.text || promiseText(item)
    ),
    openItems: next ? [next.displayText || next.text] : [],
    verification: ["Expected tests or human checks will be listed after the Task is ready."],
    remaining,
    ready: !next && criteria.length > 0,
  };
}

export function whyThisQuestionMatters(question) {
  const text = String(question?.text || question?.displayText || "").toLowerCase();
  if (/request|response|schema/.test(text)) {
    return "This changes which existing integrations could be affected.";
  }
  if (/compatib|existing|break/.test(text)) {
    return "This decides whether current users keep working without changes.";
  }
  if (/fail|error|startup/.test(text)) {
    return "This decides how people notice a mistake, instead of failing silently.";
  }
  if (/pii|retain|privacy|data/.test(text)) {
    return "This changes what data is stored and who is responsible for it.";
  }
  return "This answer changes the scope engineering will implement.";
}

export const BUILDER_STAGE_COPY = {
  understanding: "Understanding your idea",
  clarifying: "Clarifying important decisions",
  ready: "Ready for your review",
  sent: "Task sent to engineering",
};

export function trustLabelToFounder(origin) {
  return markerForOrigin(origin) || sourceTrustLabel(origin);
}

export function isMaterialProductQuestion(question, requestText = "") {
  const text = String(question?.text || question?.displayText || "").trim();
  if (!text) return false;
  if (/what is the title|optional field|fill in this template/i.test(text)) return false;
  const request = String(requestText || "").toLowerCase();
  if (/request schemas? only/.test(request) && /request|response/.test(text.toLowerCase()) && /or both/.test(text.toLowerCase())) {
    return false;
  }
  return MATERIAL_QUESTION_RE.test(text) || /or both|should this|who is affected|how should|when should/.test(text);
}

export function selectNextConversationQuestion(draft, requestText) {
  const questions = Array.isArray(draft?.unresolvedQuestions) ? draft.unresolvedQuestions : [];
  const open = questions.filter((q) => !q.status || q.status === "open");
  const partitioned = partitionHandoffQuestions({
    serverQuestions: open,
    requestText,
  });
  const founder = partitioned.founderQuestions || partitioned.founder || open;
  const material = (Array.isArray(founder) ? founder : []).filter((item) =>
    isMaterialProductQuestion(item, requestText)
  );
  const next = material[0] || null;
  return { next, remaining: Math.max(0, material.length - (next ? 1 : 0)) };
}

export function missingSummary(draft, requestText) {
  const { next, remaining } = selectNextConversationQuestion(draft, requestText);
  if (!next) {
    return "Nothing left that would change what engineering builds, what is excluded, or how success is checked.";
  }
  const first = next.displayText || next.text;
  if (!remaining) return `Highest impact: ${first}`;
  return `Highest impact: ${first} Then ${remaining} other decision${remaining === 1 ? "" : "s"} that could still change the work.`;
}

export function questionChoicesForMessage(question) {
  const suggested = suggestedAnswersForQuestion(question).slice(0, 3);
  return [...suggested, ...TASK_BUILDER_QUESTION_CHOICES];
}
